import styles from './TypeAhead.module.scss';
import { Loader } from '@components';
import { TGroupedOpts, TypeAheadOption } from '@types';
import { CheckOutlined } from '@mui/icons-material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

type TProps = {
	options: TypeAheadOption[] | string[] | TGroupedOpts | null;
	selected: (o:TypeAheadOption | string, p?:{ group: string }, shiftKey?: boolean) => void;
	noOptionsText?: string;
	isLoadingOptions: boolean;
	selectid?: string | null;
	focusedIndex?: number;
	multiSelect?: boolean;
	selectedChipValues?: any[];
}
type ListItem = {
	selectId: string | null | undefined;
	className: string;
	onClick?: (e: React.MouseEvent) => void;
	role: string;
	label: string | JSX.Element;
	disabled?: boolean;
	isSelected?: boolean;
}

export const Options = (props:TProps) => {
	const {
		options,
		selected,
		noOptionsText,
		isLoadingOptions,
		selectid,
		focusedIndex = -1,
		multiSelect = false,
		selectedChipValues = [],
	} = props;
	const isObject = (o:any) => {
		return typeof o === 'object' && !Array.isArray(o) && o !== null;
	}
	const noOptions = () => {
		if (options === null) {
			return noOptionsText ?
				<li data-selectid={selectid} className={styles.noOptsTxt}>{noOptionsText}</li> :
				<li data-selectid={selectid} className={styles.noOptsTxt}>Keep typing for more options...</li>;
		} else if (Array.isArray(options) && !options.length && !isLoadingOptions) {
			return noOptionsText ?
				<li data-selectid={selectid} className={styles.noOptsTxt}>{noOptionsText}</li> :
				<li data-selectid={selectid} className={styles.noOptsTxt}>Keep typing for more options...</li>;
		} else if (
			typeof options === 'object' &&
			!Array.isArray(options) &&
			Object.keys(options).length === 0
		) {
			return noOptionsText ?
				<li data-selectid={selectid} className={styles.noOptsTxt}>{noOptionsText}</li> :
				<li data-selectid={selectid} className={styles.noOptsTxt}>Keep typing for more options...</li>;
		}
		return null;
	}
	const buildOptions = () => {
		const opts: ListItem[] = [];
		let selectableIndex = 0;
		if (Array.isArray(options)) {
			for (let i=0, l=options.length; i<l; ++i) {
				let label;
				let disabled = false;
				let optionSelected = false;
				if (isObject(options[i])) {
					const opt = options[i] as TypeAheadOption;
					label = opt.label;  // eslint-disable-line
					disabled = opt.disabled || false;
					optionSelected = opt.selected || selectedChipValues.includes(opt.value);
				} else {
					label = options[i] as string;
					optionSelected = selectedChipValues.includes(options[i]);
				}

				// Build className based on state
				let className = styles.option;
				if (disabled) {
					className += ` ${styles.disabled}`;
				} else if (optionSelected) {
					className += ` ${styles.selected}`;
				}

				const isFocused = !disabled && selectableIndex === focusedIndex;
				if (isFocused) {
					className += ` ${styles.focused}`;
				}

				opts.push({
					selectId: selectid,
					className,
					onClick: disabled ? undefined : (e) => selected(options[i], undefined, e.shiftKey),
					role: 'option',
					label,
					disabled,
					isSelected: optionSelected
				});

				if (!disabled) selectableIndex++;
			}
		} else if (
			typeof options === 'object' &&
			!Array.isArray(options) &&
			options !== null
		) {
			for (const [key, value] of Object.entries(options)) {
				opts.push({
					selectId: selectid,
					className: styles.groupHeader,
					role: 'group',
					label: key
				});
				for (let i=0, l=value.length; i<l; ++i) {
					const group = key;
					let label;
					let disabled = false;
					let optionSelected = false;

					if (isObject(value[i])) {
						const opt = value[i] as TypeAheadOption;
						label = opt.label; // eslint-disable-line
						disabled = opt.disabled || false;
						optionSelected = opt.selected || selectedChipValues.includes(opt.value);
					} else {
						label = value[i] as string;
						optionSelected = selectedChipValues.includes(value[i]);
					}

					let className = styles.option;
					if (disabled) {
						className += ` ${styles.disabled}`;
					} else if (optionSelected) {
						className += ` ${styles.selected}`;
					}

					const isFocused = !disabled && selectableIndex === focusedIndex;
					if (isFocused) {
						className += ` ${styles.focused}`;
					}

					opts.push({
						selectId: selectid,
						className,
						onClick: disabled ? undefined : (e) => selected(value[i], { group }, e.shiftKey),
						role: 'option',
						label,
						disabled,
						isSelected: optionSelected
					});

					if (!disabled) selectableIndex++;
				}
			}
		}
		return opts;
	}
	return (
		<>
			{!isLoadingOptions && noOptions()}
			{isLoadingOptions && <li data-testid={'@Ofload-TypeAhead-Loader'} className={styles.loader}>
				<Loader size={15} thickness={3} />
			</li>}
			{!isLoadingOptions && buildOptions().map((option:ListItem, index:number) => {
				return option.role === 'group' ? <li
					key={index}
					data-selectid={option.selectId}
					className={option.className}
					role={option.role}
				>
					{option.label}
				</li> : <li
					key={index}
					data-selectid={option.selectId}
					className={option.className}
					onClick={option.onClick}
					role={option.role}
					aria-selected={option.isSelected || false}
					aria-disabled={option.disabled || false}
					style={{ cursor: option.disabled ? 'not-allowed' : 'pointer' }}
				>
					{multiSelect ? (
						<div style={{
							display: 'flex', alignItems: 'center', gap: '10px'
						}}>
							{option.isSelected
								? <CheckBoxIcon style={{
									color: 'var(--brand-orange-default)', fontSize: '18px', flexShrink: 0
								}} />
								: <CheckBoxOutlineBlankIcon style={{
									color: 'var(--neutral-04)', fontSize: '18px', flexShrink: 0
								}} />
							}
							<span>{option.label}</span>
						</div>
					) : (
						<>
							<span>{option.label}</span>
							{option.isSelected && (
								<CheckOutlined style={{ color: 'var(--brand-orange-default)', fontSize: '16px' }} />
							)}
						</>
					)}
				</li>
			})}
		</>
	);
}
